import { useState } from "react";
import { Button, Input, Select, Spinner, MessageBar } from "@fluentui/react-components";
import { Dismiss24Regular, ArrowDownload24Regular } from "@fluentui/react-icons";
import { ExportRequest } from "../../services/api/conversationApiService";
import styles from "./ConversationExporterPopup.module.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExport: (params: ExportRequest) => Promise<void>;
  isLoading: boolean;
}

export const ConversationExporterPopup = ({ isOpen, onClose, onExport, isLoading }: Props) => {
  const [count, setCount] = useState<number>(24);
  const [timeUnit, setTimeUnit] = useState<"hours" | "days">("hours");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleExport = async () => {
    if (count <= 0) {
      setErrorMessage("Duration must be greater than 0");
      return;
    }

    setErrorMessage(null);
    try {
      await onExport({ duration: count, unit: timeUnit });
    } catch (error) {
      console.error("Export failed:", error);
      setErrorMessage(error instanceof Error ? error.message : "Failed to export conversations. Please try again.");
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.popup} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Export Conversations</h2>
          <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={handleClose} disabled={isLoading} className={styles.closeButton} />
        </div>

        <div className={styles.content}>
          <div className={styles.inputSection}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Duration</label>
              <Input
                type="number"
                value={count.toString()}
                onChange={(_, data) => setCount(parseInt(data.value) || 0)}
                min={1}
                disabled={isLoading}
                className={styles.input}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>Unit</label>
              <Select value={timeUnit} onChange={(_, data) => setTimeUnit(data.value as "hours" | "days")} disabled={isLoading} className={styles.select}>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </Select>
            </div>
          </div>

          {errorMessage && (
            <div className={styles.errorMessage}>
              <MessageBar
                intent="error"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#dc3545",
                  padding: "8px 0"
                }}
              >
                {errorMessage}
              </MessageBar>
            </div>
          )}

          <div className={styles.footer}>
            <Button appearance="subtle" onClick={handleClose} disabled={isLoading} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<ArrowDownload24Regular />}
              onClick={handleExport}
              disabled={isLoading || count <= 0}
              className={styles.primaryButton}
            >
              {isLoading ? (
                <>
                  <Spinner size="tiny" />
                  Exporting...
                </>
              ) : (
                "Export"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
